// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import calculateTotal from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/CalculateTotal";
import changeProductQty from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ChangeProductQty";
import removeProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/RemoveProduct";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, { CartLineContainerStyles } from "@pace/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@pace/App/Components/Cart/LinesHeader";
import { QtyFieldStyles } from "@pace/App/Components/Qty/QtyField";
import { CustomAppState, PackItem } from "@pace/App/Store/Reducers";
import Select, { SelectPresentationProps } from "@pace/MobiusOverrides/Select";
import ProductQuantityOrdered, { ProductQuantityStyles } from "@pace/Overrides/Components/ProductQuantityOrdered";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    productInfos: state.pages.quickOrder.productInfos,
    products: state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value) as (
        | ProductModel
        | undefined
    )[],
    total: state.pages.quickOrder.total,
    currencySymbol: state.context.session.currency?.currencySymbol || "",
    showPricing: state.context.showPricing,
    // Xng-Change
    packProducts: state.xQuickOrder.packProducts,
});

const mapDispatchToProps = {
    calculateTotal,
    changeProductQty,
    removeProduct,
    // Xng-Change
    setPackProducts: (products: PackItem[]) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xQuickOrder/SetPackProducts",
            products
        } as any);
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderProductListStyles {
    wrapper?: InjectableCss;
    cardContainer?: CardContainerStyles;
    gridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    brandAndProductDescriptionGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productNumberText?: TypographyPresentationProps;
    availability?: ProductAvailabilityStyles;
    priceAndQuantityGridItem?: GridItemProps;
    priceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: ProductQuantityStyles;
    extendedPriceGridItem?: GridItemProps;
    extendedPriceText?: TypographyPresentationProps;
    removeProductGridItem?: GridItemProps;
    removeProductClickable?: ClickableProps;
    removeProductIcon?: IconPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    totalText?: TypographyPresentationProps;
    // XNG-Changes
    productNumber?: ProductPartNumbersStyles;
    priceTotal?: SmallHeadingAndTextStyles;
    cartLineContainer?: CartLineContainerStyles;
    lineHeader?: LinesHeaderStyles;
    uomSelect?: SelectPresentationProps;
}

export const quickOrderProductListStyles: QuickOrderProductListStyles = {
    wrapper: {
        css: css`
            padding-bottom: 20px;
            margin-top: 20px;
        `,
    },
    cardContainer: {
        cardDivider: {
            css: css`
                margin: 0 20px;
                width: 100%;
                border-top: 1px solid ${getColor("common.border")};
                &:last-child {
                    border-bottom: 1px solid ${getColor("common.border")};
                }
            `,
        },
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: 10px;
                    `,
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: 10px;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                ])}
        `,
    },
    productInfoGridItem: { width: [7, 7, 7, 8, 8] },
    brandAndProductDescriptionGridItem: {
        width: [12, 12, 12, 6, 5],
        css: css`
            flex-direction: column;
        `,
    },
    availability: {
        container: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    priceAndQuantityGridItem: { width: [12, 12, 12, 6, 7] },
    priceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            justify-content: center;
        `,
    },
    quantityGridItem: { width: [6, 6, 5, 5, 4] },
    extendedPriceGridItem: { width: [6, 6, 3, 3, 4] },
    extendedPriceText: {
        css: css`
            font-weight: bold;
            align-self: center;
        `,
    },
    removeProductGridItem: {
        css: css`
            text-align: center;
            display: block;
        `,
        width: [3, 3, 3, 2, 2],
    },
    removeProductClickable: {
        css: css`
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 15px;
        `,
    },
    removeProductIcon: { src: XCircle },
    totalText: {
        css: css`
            float: right;
            font-weight: bold;
            margin: 15px 0;
        `,
    },
};

const styles = quickOrderProductListStyles;
const StyledSection = getStyledWrapper("section");

const QuickOrderProductList = ({
    products,
    productInfos,
    total,
    currencySymbol,
    showPricing,
    calculateTotal,
    changeProductQty,
    removeProduct,
    // Xng-Change
    packProducts,
    setPackProducts,
}: Props) => {
    useEffect(() => calculateTotal(), [products]);

    const removeProductHandler = (productInfo: ProductInfo) => {
        removeProduct({ productId: productInfo.productId, unitOfMeasure: productInfo.unitOfMeasure });
    };

    // Xng-Change
    const handleUomChange = (event: React.FormEvent<HTMLSelectElement>, product: ProductModel, index: number) => {
        let products = [...packProducts];

        const isPack = event.currentTarget.value === "Pack";

        if(!isPack) {
            products = products.filter(item => item.id !== product.id);
        } else {
            products.push({
                id: product.id,
                packQty: parseInt(product.packDescription, 10),
            });
        }
        setPackProducts(products);

        const packQty = parseInt(product.packDescription, 10);
        const productInfo = productInfos[index];

        changeProductQty({
            qtyOrdered: productInfo.qtyOrdered,
            productId: product.id,
            unitOfMeasure: productInfo.unitOfMeasure,
            product,
            packQty: isPack ? productInfo.qtyOrdered * packQty : productInfo.qtyOrdered,
        } as any);
    };

    if (!products || products.length === 0) {
        return null;
    }

    const productListDisplay = products.map((product, index) => {
        if (!product || index >= productInfos.length) {
            return;
        }

        const productInfo = productInfos[index];
        const productContext: ProductContextModel = {
            product,
            productInfo,
            onQtyOrderedChanged: qtyOrdered => {
                /**
                 * Xng-Change PAC-1255
                 * handle pack qty
                 */
                const isPack = packProducts.findIndex(p => p.id === product.id) > -1;
                const packQty = parseInt(product.packDescription, 10);
                changeProductQty({
                    qtyOrdered,
                    productId: product.id,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    product,
                    packQty: isPack ? qtyOrdered * packQty : qtyOrdered,
                } as any);
            },
        };
        // XNG-Changes: restructuring using cartlinecontainer
        return (
            <ProductContext.Provider value={productContext} key={product.id + productInfo.unitOfMeasure}>
                <CartLineContainer
                    data-test-selector={`QuickOrderProductList_ProductId_${product.id}`}
                    extendedStyles={styles.cartLineContainer}
                >
                    <CartLineContainer.Image>
                        <ProductImage product={productContext} extendedStyles={styles.productImage} />
                    </CartLineContainer.Image>
                    <CartLineContainer.Description>
                        <ProductBrand brand={product.brand!} extendedStyles={styles.brand} />
                        <ProductDescription product={productContext} extendedStyles={styles.productDescription} />
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            extendedStyles={styles.productNumber}
                        />
                    </CartLineContainer.Description>
                    <CartLineContainer.Availability>
                        {!product.quoteRequired && <ProductContextAvailability extendedStyles={styles.availability} />}
                    </CartLineContainer.Availability>
                    <CartLineContainer.Qty>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                        {/* Xng-Change */}
                        {product.packDescription && parseInt(product.packDescription, 10) > 1 && product.unitOfMeasures &&
                            <Select
                                {...styles.uomSelect}
                                value={packProducts.find(p => p.id === product.id) ? "Pack" : product.unitOfMeasures[0].unitOfMeasure}
                                onChange={(event: React.FormEvent<HTMLSelectElement>) =>
                                    handleUomChange(event, product, index)
                                }
                                data-test-selector="QuickOrder_UOM_Select"
                            >
                                <option value={product.unitOfMeasures[0].unitOfMeasure}>{product.unitOfMeasures[0].unitOfMeasure}</option>
                                <option value="Pack">{translate("Pack ({0} units)", product.packDescription)}</option>
                            </Select>
                        }
                    </CartLineContainer.Qty>
                    <CartLineContainer.Price>
                        <ProductPrice
                            product={productContext}
                            currencySymbol={currencySymbol}
                            showSavings={false}
                            showLabel={true}
                            extendedStyles={styles.price}
                            // Xng-Changes
                            showPack={false}
                        />
                    </CartLineContainer.Price>
                    <CartLineContainer.Total>
                        {!product.quoteRequired && !productInfo.pricing?.requiresRealTimePrice && showPricing && (
                            <SmallHeadingAndText
                                {...styles.priceTotal}
                                heading={translate("Total")}
                                text={productInfo.pricing?.extendedUnitNetPriceDisplay as string}
                            />
                        )}
                    </CartLineContainer.Total>
                    <CartLineContainer.Remove>
                        <Clickable
                            {...styles.removeProductClickable}
                            onClick={() => removeProductHandler(productInfo)}
                            data-test-selector="quickOrder_RemoveProduct"
                            aria-label="quick Order Remove Product"
                        >
                            <IconMemo {...styles.removeProductIcon} />
                        </Clickable>
                    </CartLineContainer.Remove>
                    <CartLineContainer.Actions>
                        <ProductAddToListLink extendedStyles={styles.addToListLink} />
                    </CartLineContainer.Actions>
                </CartLineContainer>
            </ProductContext.Provider>
        );
    });

    return (
        <>
            <LinesHeader extendedStyles={styles.lineHeader} />
            <StyledSection {...styles.wrapper}>{productListDisplay}</StyledSection>
            {showPricing && (
                <Typography {...styles.totalText} data-test-selector="quickOrder_TotalPrice">
                    {`${translate("Total")}`} <LocalizedCurrency amount={total} />
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductList),
    definition: {
        group: "Quick Order",
        icon: "List",
        allowedContexts: ["QuickOrderPage"],
    },
};

export default widgetModule;
